import React, { useEffect, useRef } from "react";

const STICKY_VH = 90; // height of the pinned viewport (reduce/increase to taste)
const COLORS = {
  pale: "#95B4CC",
  accent: "#6B8BA4",
  primary: "#2B4C65",
  ink: "#1D1C22",
  white: "#FFFFFF",
};
const fromPublic = (p) => `${process.env.PUBLIC_URL}${p}`;

/* tune how much vertical scroll advances ONE slide (in viewport heights) */
const SCROLL_PER_SLIDE_VH = 150;

const SLIDES = [
  {
    key: "research",
    title: "Research-led preparation",
    bullets: ["Map facts to forum, statute, timeline.", "Drafts built to survive scrutiny."],
    chips: ["Written submissions", "Briefs", "Affidavits"],
    img: fromPublic("/home/why-research.jpg"),
  },
  {
    key: "courtroom",
    title: "Courtroom-ready execution",
    bullets: ["Clean, complete, on-time filings.", "Clear remedies and next steps."],
    chips: ["Supreme Court", "High Courts", "Tribunals"],
    img: fromPublic("/home/why-courtroom.jpg"),
  },
  {
    key: "crossborder",
    title: "Cross-border fluency",
    bullets: ["India, UK, US coordination.", "Practical help on docs & waivers."],
    chips: ["India", "United Kingdom", "United States"],
    img: fromPublic("/home/why-crossborder.jpg"),
  },
];

export default function WhyChooseScroller() {
  const wrapRef = useRef(null);     // the tall wrapper that enables scrolling
  const stickyRef = useRef(null);   // the pinned viewport inside gutters
  const trackRef = useRef(null);    // horizontal slides row

  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const wrap = wrapRef.current;
    const sticky = stickyRef.current;
    const track = trackRef.current;
    if (!wrap || !sticky || !track) return;

    let slideW = 0;
    let current = 0;
    let animating = false;
    let pending = null;

    /* fixed wrapper height: (slides-1) steps * SCROLL_PER_SLIDE_VH */
    const setHeights = () => {
        if (!mql.matches) {
          wrap.style.height = "auto";
          track.style.transform = "translate3d(0,0,0)";
          return;
        }
        const vh = window.innerHeight;
        const steps = SLIDES.length - 1;
      
        // ✅ wrapper height = sticky height + steps * per-step distance
        wrap.style.height = `${STICKY_VH + steps * SCROLL_PER_SLIDE_VH}vh`;
      
        // slide width matches pinned container width
        slideW = sticky.getBoundingClientRect().width || 0;
      
        // keep aligned after resize
        track.style.transform = `translate3d(${-current * slideW}px,0,0)`;
      };
      
      const vh = window.innerHeight;
      const steps = SLIDES.length - 1;
      wrap.style.height = `${steps * SCROLL_PER_SLIDE_VH}vh`; // tall but predictable

      // slide width = sticky container width; CSS handles slide width via w-full
      slideW = sticky.getBoundingClientRect().width || 0;
      // keep aligned after resize
      track.style.transform = `translate3d(${-current * slideW}px,0,0)`;
    

    const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

    const goTo = (idx) => {
      if (!mql.matches) return;
      idx = clamp(idx, 0, SLIDES.length - 1);
      if (idx === current) return;
      if (animating) { pending = idx; return; }

      animating = true;
      track.style.transition = "transform 650ms cubic-bezier(.22,.61,.36,1)";
      track.style.transform = `translate3d(${-idx * slideW}px,0,0)`;

      const onEnd = () => {
        track.removeEventListener("transitionend", onEnd);
        track.style.transition = "none";
        current = idx;
        animating = false;
        if (pending != null && pending !== current) {
          const next = pending; pending = null; goTo(next);
        }
      };
      track.addEventListener("transitionend", onEnd);
    };

    const onScroll = () => {
        if (!mql.matches) return;
      
        const vh = window.innerHeight;
        const steps = SLIDES.length - 1;
        const perStepPx = (SCROLL_PER_SLIDE_VH / 100) * vh;
      
        const rect = wrap.getBoundingClientRect();
        const entered = Math.max(0, -rect.top); // px we’ve scrolled into the wrapper
      
        // clamp to the total runway (so the last slide is fully reachable)
        const runway = steps * perStepPx;
        const raw = Math.min(entered, runway) / Math.max(1, perStepPx); // 0..steps
        const targetIdx = Math.max(0, Math.min(SLIDES.length - 1, Math.round(raw)));
      
        const step =
          targetIdx > current ? current + 1 : targetIdx < current ? current - 1 : current;
        goTo(step);
      };
      

    // init
    requestAnimationFrame(setHeights);
    window.addEventListener("resize", setHeights, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    mql.addEventListener?.("change", setHeights);

    return () => {
      window.removeEventListener("resize", setHeights);
      window.removeEventListener("scroll", onScroll);
      mql.removeEventListener?.("change", setHeights);
    };
  }, []);

  return (
    <section aria-label="Why Choose Rex Counsel">
      {/* header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6">
        <div className="text-[11px] tracking-widest uppercase text-slate-500 mb-1">Our Approach</div>
        <h2 className="text-2xl md:text-3xl font-semibold" style={{ color: COLORS.ink }}>
          Why Choose Rex Counsel
        </h2>
        <div
          className="h-px mt-2 bg-gradient-to-r from-[var(--pale)] via-[var(--accent)] to-transparent"
          style={{ "--pale": COLORS.pale, "--accent": COLORS.accent }}
        />
      </div>

      {/* DESKTOP: pinned scroller inside page gutters */}
      <div className="hidden lg:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* tall wrapper creates the scroll runway; sticky stays pinned within it */}
          <div ref={wrapRef} className="relative">
          <div ref={stickyRef} className="sticky top-0 overflow-hidden rounded-2xl" style={{ height: `${STICKY_VH}vh` }}>
              <div
                ref={trackRef}
                className="relative h-full flex flex-row flex-nowrap will-change-transform select-none"
                style={{ transform: "translate3d(0,0,0)" }}
              >
                {SLIDES.map((s) => (
                  <Slide key={s.key} s={s} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE/TABLET: swipe cards within gutters */}
      <div className="lg:hidden">
        <div className="relative overflow-x-auto snap-x snap-mandatory no-scrollbar px-4 sm:px-6">
          <div className="flex gap-4 pb-6">
            {SLIDES.map((s) => (
              <div key={s.key} className="snap-center shrink-0 w-[85%]">
                <Card s={s} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Slide({ s }) {
  return (
    <div className="relative h-full flex-none w-full">
      <img
        src={s.img}
        alt={s.title}
        className="absolute inset-0 w-full h-full object-cover object-center"
        onError={(e) => (e.currentTarget.style.opacity = 0.2)}
      />
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(135deg, rgba(107,139,164,0.55), rgba(43,76,101,0.65))" }}
      />
      <div className="absolute inset-0 flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="max-w-xl rounded-2xl p-[1px]" style={{ background: `linear-gradient(135deg, ${COLORS.pale}, ${COLORS.accent})` }}>
            <div className="rounded-2xl bg-white/90 backdrop-blur-sm p-6">
              <h3 className="text-xl md:text-2xl font-semibold" style={{ color: COLORS.primary }}>{s.title}</h3>
              <ul className="mt-3 space-y-1 text-slate-800 text-[15px]">
                {s.bullets.map((b, i) => <li key={i}>• {b}</li>)}
              </ul>
              <div className="mt-4 flex flex-wrap gap-2">
                {s.chips.map((c) => (
                  <span key={c} className="text-xs px-2.5 py-1 rounded-full" style={{ background: COLORS.pale, color: COLORS.primary }}>
                    {c}
                  </span>
                ))}
              </div>
              <div className="mt-5">
                <a href="#contact" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium" style={{ background: COLORS.primary, color: COLORS.white }}>
                  Book a Consultation
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-4 right-6 text-[8rem] leading-none select-none" style={{ color: COLORS.primary, opacity: 0.08 }}>⚖</div>
    </div>
  );
}

function Card({ s }) {
  return (
    <div className="rounded-2xl overflow-hidden border shadow-sm" style={{ borderColor: COLORS.pale }}>
      <div className="relative h-40">
        <img
          src={s.img}
          alt={s.title}
          className="absolute inset-0 w-full h-full object-cover object-center"
          onError={(e) => (e.currentTarget.style.opacity = 0.2)}
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(107,139,164,0.45), rgba(43,76,101,0.55))" }} />
      </div>
      <div className="p-4 bg-white">
        <h3 className="text-base font-semibold" style={{ color: COLORS.primary }}>{s.title}</h3>
        <ul className="mt-2 space-y-1 text-slate-800 text-sm">
          {s.bullets.map((b, i) => <li key={i}>• {b}</li>)}
        </ul>
        <div className="mt-3 flex flex-wrap gap-2">
          {s.chips.map((c) => (
            <span key={c} className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: COLORS.pale, color: COLORS.primary }}>
              {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
